/**
 * Map System — Shared Utilities
 *
 * Pure functions for coordinate math, travel calculations, hierarchy building,
 * grid snapping, and SVG detection. No side effects, no React, no Supabase.
 */

import type { MapNode, TravelResult, ViewportState, GridConfig, WorldMap } from "./types";
import { LOCATION_ENTITY_TYPES, CHILD_TO_PARENT_RELS, PARENT_TO_CHILD_RELS, ZOOM_LIMITS } from "./types";

// ─── SVG Detection ───

export function isSvgUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return url.endsWith(".svg") || url.includes("image/svg");
}

// ─── Coordinate Transforms ───

/** Convert a mouse event to normalized 0–1 map coordinates */
export function getMapCoords(
  e: React.MouseEvent,
  mapElement: HTMLElement | null,
): { x: number; y: number } | null {
  if (!mapElement) return null;
  const rect = mapElement.getBoundingClientRect();
  const x = (e.clientX - rect.left) / rect.width;
  const y = (e.clientY - rect.top) / rect.height;
  return {
    x: Math.max(0, Math.min(1, x)),
    y: Math.max(0, Math.min(1, y)),
  };
}

/** Clamp zoom within allowed range */
export function clampZoom(zoom: number): number {
  return Math.max(ZOOM_LIMITS.min, Math.min(ZOOM_LIMITS.max, zoom));
}

/** Apply a zoom delta (from scroll wheel) */
export function applyZoomDelta(current: number, delta: number): number {
  const step = delta > 0 ? -0.15 : 0.15;
  return clampZoom(current + step);
}

// ─── Grid Snapping ───

/** Snap a coordinate to the nearest grid intersection */
export function snapToGrid(value: number, gridSize: number): number {
  const step = 1 / gridSize;
  return Math.round(value / step) * step;
}

/** Snap x,y coordinates to grid if enabled */
export function snapCoords(
  coords: { x: number; y: number },
  grid: GridConfig,
): { x: number; y: number } {
  if (!grid.enabled || !grid.snap) return coords;
  return {
    x: snapToGrid(coords.x, grid.size),
    y: snapToGrid(coords.y, grid.size),
  };
}

// ─── Euclidean Distance & Travel ───

/** Calculate Euclidean distance between two normalized coordinates */
export function euclideanDistance(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

/** Calculate travel time between two map nodes */
export function calculateTravel(
  from: { x: number; y: number },
  to: { x: number; y: number },
  config: {
    worldScale?: number;
    distanceUnit?: string;
    variancePercent?: number;
    speedModifier?: number;
  } = {},
): TravelResult {
  const {
    worldScale = 100,
    distanceUnit = "leagues",
    variancePercent = 20,
    speedModifier = 1,
  } = config;

  const normalizedDist = euclideanDistance(from, to);
  const distance = normalizedDist * worldScale;

  // Apply variance: ±variancePercent%
  const varianceFactor = 1 + (Math.random() * 2 - 1) * (variancePercent / 100);
  const travelTime = (distance / speedModifier) * varianceFactor;

  return {
    distance: Math.round(distance * 100) / 100,
    unit: distanceUnit,
    travelTime: Math.round(travelTime * 100) / 100,
    varianceApplied: Math.round((varianceFactor - 1) * 100),
  };
}

// ─── Location Entity Helpers ───

/** Check if an entity type is location-like */
export function isLocationEntity(type: string): boolean {
  return LOCATION_ENTITY_TYPES.has(type);
}

/** Filter entities to only location types */
export function filterLocationEntities(entities: any[]): any[] {
  return entities.filter(
    (e) => isLocationEntity(e.type) && !e.deleted_at,
  );
}

/** Get location entities that are children of a specific parent entity (recursive — includes all descendants) */
export function getChildLocations(
  entities: any[],
  parentEntityId: string | null,
  allEntities: any[],
): any[] {
  if (!parentEntityId) return filterLocationEntities(entities);

  const parentEntity = allEntities.find((e) => e.id === parentEntityId);
  if (!parentEntity) return filterLocationEntities(entities);

  const locations = filterLocationEntities(entities);
  const byId = new Map(locations.map((e) => [e.id, e]));
  const byName = new Map<string, any>();
  for (const e of locations) {
    if (e.name) byName.set(e.name.toLowerCase(), e);
  }

  // Build parent→children adjacency from relationships
  const childrenOf = new Map<string, Set<string>>();
  for (const e of locations) {
    for (const r of (e.relationships || [])) {
      if (!CHILD_TO_PARENT_RELS.has(r.type)) continue;
      const parentId = r.target_id || byName.get((r.target_name || "").toLowerCase())?.id;
      if (!parentId) continue;
      if (!childrenOf.has(parentId)) childrenOf.set(parentId, new Set());
      childrenOf.get(parentId)!.add(e.id);
    }
  }

  // BFS to collect all descendants
  const result = new Set<string>();
  const queue = [parentEntityId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const kids = childrenOf.get(current);
    if (!kids) continue;
    for (const kidId of kids) {
      if (!result.has(kidId)) {
        result.add(kidId);
        queue.push(kidId);
      }
    }
  }

  return locations.filter((e) => result.has(e.id));
}

// ─── Location Hierarchy (for tree view) ───

export interface LocationHierarchy {
  roots: any[];
  childrenMap: Map<string, any[]>;
}

/** Build a parent→children map from location entity relationships.
 *  Processes BOTH directions:
 *  - CHILD_TO_PARENT_RELS (e.g. "contained_in") → child points to parent
 *  - PARENT_TO_CHILD_RELS (e.g. "contains")     → parent points to child
 */
export function buildLocationHierarchy(locations: any[]): LocationHierarchy {
  const childrenMap = new Map<string, any[]>();
  const childIds = new Set<string>();
  const entityById = new Map(locations.map((e: any) => [e.id, e]));
  const entityByName = new Map<string, any>();
  for (const e of locations) {
    entityByName.set(e.name?.toLowerCase(), e);
  }

  const resolveEntity = (targetId?: string, targetName?: string): any | undefined => {
    return (targetId && entityById.get(targetId)) ||
           (targetId && entityByName.get(targetId.toLowerCase())) ||
           (targetName && entityByName.get(targetName.toLowerCase())) ||
           undefined;
  };

  const addChild = (parentId: string, childId: string) => {
    if (parentId === childId) return; // prevent self-nesting
    if (childIds.has(childId)) return; // already assigned to a parent
    const child = entityById.get(childId);
    if (!child) return;
    childIds.add(childId);
    const list = childrenMap.get(parentId) || [];
    if (!list.find((c: any) => c.id === childId)) list.push(child);
    childrenMap.set(parentId, list);
  };

  for (const entity of locations) {
    const rels = (entity.relationships || []) as { target_id?: string; target_name?: string; type: string }[];
    for (const rel of rels) {
      if (CHILD_TO_PARENT_RELS.has(rel.type)) {
        // This entity is a child of target — find the parent
        const parent = resolveEntity(rel.target_id, rel.target_name);
        if (parent) addChild(parent.id, entity.id);
      } else if (PARENT_TO_CHILD_RELS.has(rel.type)) {
        // This entity is a parent of target — find the child
        const child = resolveEntity(rel.target_id, rel.target_name);
        if (child) addChild(entity.id, child.id);
      }
    }
  }

  const roots = locations.filter((e: any) => !childIds.has(e.id));
  return { roots, childrenMap };
}

// ─── Map Node Helpers ───

/** Filter nodes at a specific level of the hierarchy (legacy parent_node_id) */
export function getNodesAtLevel(nodes: MapNode[], parentId: string | null): MapNode[] {
  return nodes.filter((n) => n.parent_node_id === parentId);
}

/**
 * Filter nodes belonging to a specific map object.
 *
 * @param variantId - When provided, only shared nodes (variant_id IS NULL) and
 *   nodes specific to this variant are returned. When omitted (or null), all
 *   nodes for the map are returned — use only when no active variant is known.
 */
export function getNodesForMap(nodes: MapNode[], mapId: string, variantId?: string | null): MapNode[] {
  const forMap = nodes.filter((n) => n.map_id === mapId);
  if (!variantId) return forMap;
  // Keep shared nodes (no variant) + nodes that belong to the active variant
  return forMap.filter((n) => !n.variant_id || n.variant_id === variantId);
}

/** Get placed entity IDs from nodes at a given level */
export function getPlacedEntityIds(nodes: MapNode[], parentId: string | null): Set<string> {
  return new Set(
    getNodesAtLevel(nodes, parentId)
      .filter((n) => n.entity_id)
      .map((n) => n.entity_id!),
  );
}

/** Get placed entity IDs from nodes on a specific map (respects active variant when provided) */
export function getPlacedEntityIdsForMap(nodes: MapNode[], mapId: string, variantId?: string | null): Set<string> {
  return new Set(
    getNodesForMap(nodes, mapId, variantId)
      .filter((n) => n.entity_id)
      .map((n) => n.entity_id!),
  );
}

/** Build a hierarchical tree of maps */
export function buildMapTree(maps: WorldMap[]): { roots: WorldMap[]; childrenMap: Map<string, WorldMap[]> } {
  const childrenMap = new Map<string, WorldMap[]>();
  const childIds = new Set<string>();
  for (const m of maps) {
    if (m.parent_map_id) {
      childIds.add(m.id);
      const list = childrenMap.get(m.parent_map_id) || [];
      list.push(m);
      childrenMap.set(m.parent_map_id, list);
    }
  }
  const roots = maps.filter(m => !childIds.has(m.id) || !m.parent_map_id);
  return { roots, childrenMap };
}

// ─── Area Preview ───

export interface AreaPreview {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Calculate area preview from start and current mouse position */
export function calculateAreaPreview(
  start: { x: number; y: number },
  current: { x: number; y: number },
): AreaPreview {
  return {
    x: Math.min(start.x, current.x),
    y: Math.min(start.y, current.y),
    w: Math.abs(current.x - start.x),
    h: Math.abs(current.y - start.y),
  };
}

/** Check if an area preview is large enough to be valid */
export function isValidArea(preview: AreaPreview, minSize = 0.01): boolean {
  return preview.w >= minSize && preview.h >= minSize;
}

// ─── Geographic Context Extraction ───

/** Known geographic regions/countries for disambiguation */
const GEO_KEYWORDS: { pattern: RegExp; hint: string }[] = [
  // Middle East & North Africa
  { pattern: /\b(ancient\s+)?(egypt|egyptian|pharao|nile|cairo|alexandria)\b/i, hint: "Egypt" },
  { pattern: /\b(israel|israeli|judea|judean|galilee|jerusalem|hebrew|canaan|canaanite)\b/i, hint: "Israel" },
  { pattern: /\b(mesopotamia|babylon|assyria|sumer|iraq|iraqi|tigris|euphrates)\b/i, hint: "Iraq" },
  { pattern: /\b(persia|persian|iran|iranian)\b/i, hint: "Iran" },
  { pattern: /\b(levant|syria|syrian|damascus|phoenicia|phoenician|lebanon|lebanese)\b/i, hint: "Lebanon" },
  { pattern: /\b(arabia|arabian|saudi|mecca|medina)\b/i, hint: "Saudi Arabia" },
  { pattern: /\b(ottoman|turkey|turkish|anatolia|constantinople|istanbul|byzantium)\b/i, hint: "Turkey" },
  { pattern: /\b(carthage|tunisia|tunisian|maghreb)\b/i, hint: "Tunisia" },
  { pattern: /\b(morocco|moroccan)\b/i, hint: "Morocco" },
  // Europe
  { pattern: /\b(roman\s+empire|ancient\s+rome|roma|latin|italia)\b/i, hint: "Italy" },
  { pattern: /\b(greece|greek|hellas|hellenic|athens|sparta|macedon)\b/i, hint: "Greece" },
  { pattern: /\b(france|french|gaul|paris)\b/i, hint: "France" },
  { pattern: /\b(england|english|britain|british|london)\b/i, hint: "United Kingdom" },
  { pattern: /\b(germany|german|germanic|prussia)\b/i, hint: "Germany" },
  { pattern: /\b(spain|spanish|iberia|iberian|castile|andalusia)\b/i, hint: "Spain" },
  { pattern: /\b(portugal|portuguese)\b/i, hint: "Portugal" },
  { pattern: /\b(scandinavia|norse|viking|norway|sweden|denmark)\b/i, hint: "Norway" },
  { pattern: /\b(russia|russian|moscow|siberia)\b/i, hint: "Russia" },
  // Asia
  { pattern: /\b(china|chinese|ming|qing|tang|han dynasty|beijing|shanghai)\b/i, hint: "China" },
  { pattern: /\b(japan|japanese|edo|tokyo|kyoto|samurai|shogun)\b/i, hint: "Japan" },
  { pattern: /\b(india|indian|mughal|delhi|hindu|sanskrit)\b/i, hint: "India" },
  { pattern: /\b(korea|korean|seoul|joseon)\b/i, hint: "South Korea" },
  { pattern: /\b(mongol|mongolia)\b/i, hint: "Mongolia" },
  // Africa
  { pattern: /\b(africa|african|sahara|sub-saharan)\b/i, hint: "Africa" },
  { pattern: /\b(ethiopia|ethiopian|axum|aksum)\b/i, hint: "Ethiopia" },
  // Americas
  { pattern: /\b(aztec|maya|inca|mesoamerica)\b/i, hint: "Mexico" },
  // Broader regions
  { pattern: /\b(middle\s*east|near\s*east)\b/i, hint: "Middle East" },
  { pattern: /\b(mediterranean)\b/i, hint: "Mediterranean" },
  { pattern: /\b(europe|european)\b/i, hint: "Europe" },
  { pattern: /\b(asia|asian)\b/i, hint: "Asia" },
];

/**
 * Extract a geographic context hint from a world's setting/description text.
 * Returns the most specific country/region name found, or null.
 */
export function extractGeoContext(settingText: string): string | null {
  if (!settingText) return null;
  // Try specific countries first (they come first in the list), return first match
  for (const { pattern, hint } of GEO_KEYWORDS) {
    if (pattern.test(settingText)) return hint;
  }
  return null;
}

/**
 * Strip parenthetical metadata from entity names for cleaner geocoding.
 * "Galilee (Region, 100 BC)" → "Galilee"
 */
export function cleanGeoName(rawName: string): string {
  return rawName.replace(/\s*\([^)]*\)\s*/g, "").trim();
}

/**
 * Build a context-aware geocoding query from entity name + world setting.
 */
export function buildGeoQuery(rawName: string, settingText: string): string {
  let clean = cleanGeoName(rawName);
  const hint = extractGeoContext(settingText);
  if (hint && !clean.toLowerCase().includes(hint.toLowerCase())) {
    clean = `${clean}, ${hint}`;
  }
  return clean;
}

/**
 * Build a Nominatim URL with optional viewbox bounds constraint.
 * bounds format: [[west, south], [east, north]]
 */
export function buildNominatimUrl(
  query: string,
  bounds?: [[number, number], [number, number]] | null,
  bounded = true,
): string {
  let url = `https://nominatim.openstreetmap.org/search?format=json&limit=3&q=${encodeURIComponent(query)}`;
  if (bounds) {
    // viewbox=left,top,right,bottom → west,north,east,south
    url += `&viewbox=${bounds[0][0]},${bounds[1][1]},${bounds[1][0]},${bounds[0][1]}`;
    if (bounded) url += `&bounded=1`;
  }
  return url;
}

/**
 * Haversine distance in km between two lat/lng points.
 */
export function haversineKm(
  lat1: number, lng1: number,
  lat2: number, lng2: number,
): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
